import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOidcUser, useOidc } from '@axa-fr/react-oidc';

import { Tooltip } from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PersonIcon from '@mui/icons-material/Person';

import LanguageSelector from '../languageSelector/LanguageSelector';
import avatar_default from 'assets/img/default-avatar.png';
import userMenuConfig from 'constants/userMenuConfig';
import {
  Avatar,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuList,
  StyledArrowDropDown,
  StyledArrowDropUp,
  StyledMenuItem,
  StyledNavLinkMenu,
  StyledSelector
} from './UserMenuStyle';
import UserMenuItem from './UserMenuItem';
import { getOidcConfigName } from 'utils/functions';
import { root } from 'utils/auth/authConfig';
import { useAuthUserData } from 'hooks';
import { useSubscription } from '@apollo/client';
import { USER_ADDITIONAL_DATA_UPDATED } from 'features/profile/subscriptions';

function UserMenu({ drawerOpen, language, changeLanguage, withGradient }) {
  const [openAvatar, setOpenAvatar] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();
  const { oidcUser } = useOidcUser(getOidcConfigName());
  const { logout } = useOidc(getOidcConfigName());
  const { authUser } = useAuthUserData();
  const [avatar, setAvatar] = useState(avatar_default);

  const activeRoute = useCallback(routeName => location.pathname.indexOf(routeName) > -1, [location.pathname]);
  const userMenuItems = userMenuConfig;

  const openCollapseAvatar = useCallback(
    e => {
      setOpenAvatar(!openAvatar);
      e.preventDefault();
    },
    [openAvatar]
  );

  const logoutAction = useCallback(
    e => {
      e.preventDefault();
      logout(root);
    },
    [logout]
  );

  useEffect(() => {
    if (authUser?.userAdditionalData?.avatar) setAvatar(authUser?.userAdditionalData?.avatar);
  }, [authUser?.userAdditionalData?.avatar]);

  useSubscription(USER_ADDITIONAL_DATA_UPDATED, {
    onData: ({ data: { data } }) => {
      setAvatar(data.userAdditionalDataUpdated.avatar);
    }
  });

  const userName = useMemo(() => {
    const person = authUser?.person;
    if (person) return `${person.firstName} ${person.lastName}`;
    return authUser?.userName || 'User';
  }, [authUser]);

  return (
    <List>
      <ListItem>
        <StyledNavLinkMenu to={'/'} withGradient={withGradient} onClick={openCollapseAvatar}>
          <ListItemIcon>
            <Avatar src={avatar} alt='...' />
          </ListItemIcon>
          <ListItemText
            primary={userName}
            secondary={openAvatar ? <StyledArrowDropUp drawerOpen={drawerOpen} /> : <StyledArrowDropDown drawerOpen={drawerOpen} />}
            disableTypography={true}
            drawerOpen={drawerOpen}
          />
        </StyledNavLinkMenu>
        <Collapse in={openAvatar} unmountOnExit>
          <MenuList>
            {userMenuItems.map((userMenu, key) => {
              return (
                <UserMenuItem key={key} userMenu={userMenu} drawerOpen={drawerOpen} activeRoute={activeRoute} withGradient={withGradient} />
              );
            })}
            {oidcUser && (
              <>
                <Tooltip disableHoverListener={drawerOpen} title={t('MyProfile')}>
                  <StyledMenuItem>
                    <StyledNavLinkMenu to={'/my-profile'} withGradient={withGradient}>
                      <ListItemIcon>
                        <PersonIcon />
                      </ListItemIcon>
                      <ListItemText primary={t('MyProfile')} disableTypography={true} drawerOpen={drawerOpen} />
                    </StyledNavLinkMenu>
                  </StyledMenuItem>
                </Tooltip>
                <Tooltip disableHoverListener={drawerOpen} title={t('Tooltips.Logout')}>
                  <StyledMenuItem>
                    <StyledNavLinkMenu to={'/'} withGradient={withGradient} onClick={logoutAction}>
                      <ListItemIcon>
                        <PowerSettingsNewIcon />
                      </ListItemIcon>
                      <ListItemText primary={t('Tooltips.Logout')} disableTypography={true} drawerOpen={drawerOpen} />
                    </StyledNavLinkMenu>
                  </StyledMenuItem>
                </Tooltip>
              </>
            )}
            <StyledSelector>
              <LanguageSelector language={language} changeLanguage={changeLanguage} drawerOpen={drawerOpen} />
            </StyledSelector>
          </MenuList>
        </Collapse>
      </ListItem>
    </List>
  );
}

UserMenu.propTypes = {
  drawerOpen: PropTypes.bool.isRequired,
  changeLanguage: PropTypes.func.isRequired,
  language: PropTypes.string.isRequired,
  withGradient: PropTypes.bool.isRequired
};

export default UserMenu;
